import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

// --- Types ---

export interface CatalogoItem {
  id: number;
  nombre: string;
  codigo?: string;
}

interface CatalogosResponse {
  success: true;
  data: CatalogoItem[];
}

// --- Query Keys ---

export const catalogosKeys = {
  empresas: ['catalogos', 'empresas'] as const,
  ubicaciones: ['catalogos', 'ubicaciones'] as const,
  tipoOt: ['catalogos', 'tipo-ot'] as const,
  tipoMantenimiento: ['catalogos', 'tipo-mantenimiento'] as const,
  subcontratos: ['catalogos', 'subcontratos'] as const,
  turnos: ['catalogos', 'turnos'] as const,
  pautas: ['catalogos', 'pautas'] as const,
  superintendencias: ['catalogos', 'superintendencias'] as const,
  gerencias: ['catalogos', 'gerencias'] as const,
};

const STALE = 5 * 60 * 1000; // 5 min — catalogs rarely change

function makeCatalogHook(key: readonly string[], endpoint: string) {
  return () =>
    useQuery({
      queryKey: key,
      queryFn: () => apiClient<CatalogosResponse>(endpoint).then((r) => r.data),
      staleTime: STALE,
    });
}

export const useEmpresas = makeCatalogHook(catalogosKeys.empresas, '/catalogos/empresas');

export function useUbicaciones(ambitoId?: number | null) {
  const qs = ambitoId ? `?ambitoId=${ambitoId}` : '';
  return useQuery({
    queryKey: [...catalogosKeys.ubicaciones, ambitoId ?? null] as const,
    queryFn: () => apiClient<CatalogosResponse>(`/catalogos/ubicaciones${qs}`).then((r) => r.data),
    staleTime: STALE,
  });
}
export const useTipoOt = makeCatalogHook(catalogosKeys.tipoOt, '/catalogos/tipo-ot');
export const useTipoMantenimiento = makeCatalogHook(
  catalogosKeys.tipoMantenimiento,
  '/catalogos/tipo-mantenimiento',
);
export const useSubcontratos = makeCatalogHook(
  catalogosKeys.subcontratos,
  '/catalogos/subcontratos',
);
export const useTurnos = makeCatalogHook(catalogosKeys.turnos, '/catalogos/turnos');

// Pautas are scoped per-ámbito when an ambitoId is given (Pauta has no ambito_id
// column; the backend derives the list from the ámbito's ACTIVE plan_matriz rows —
// see GET /catalogos/pautas). With no ambitoId the full active list is returned, for
// ámbito-agnostic callers like the plan-matriz editor. Mirrors useUbicaciones.
export function usePautas(ambitoId?: number | null) {
  const qs = ambitoId ? `?ambitoId=${ambitoId}` : '';
  return useQuery({
    queryKey: [...catalogosKeys.pautas, ambitoId ?? null] as const,
    queryFn: () => apiClient<CatalogosResponse>(`/catalogos/pautas${qs}`).then((r) => r.data),
    staleTime: STALE,
  });
}
export const useSuperintendencias = makeCatalogHook(
  catalogosKeys.superintendencias,
  '/catalogos/superintendencias',
);
export const useGerencias = makeCatalogHook(
  catalogosKeys.gerencias,
  '/catalogos/gerencias',
);

// --- CatalogoEco (BTU/ECO) ---

export interface CatalogoEcoItem {
  id: number;
  item: string;
  eco: string;
  unitario: number;
  unidad: string | null;
  ambitoId: number | null;
}

interface CatalogoEcoResponse {
  success: true;
  data: CatalogoEcoItem[];
}

export function useCatalogoEco(ambitoId?: number, tipoEquipoId?: number) {
  const params = new URLSearchParams();
  if (ambitoId) params.set('ambitoId', String(ambitoId));
  if (tipoEquipoId) params.set('tipoEquipoId', String(tipoEquipoId));
  const qs = params.toString();
  return useQuery({
    queryKey: ['catalogos', 'catalogoeco', ambitoId, tipoEquipoId] as const,
    queryFn: () => apiClient<CatalogoEcoResponse>(`/catalogoeco${qs ? `?${qs}` : ''}`).then((r) => r.data),
    staleTime: STALE,
    enabled: ambitoId != null && ambitoId > 0,
  });
}

// --- TipoEquipo ---

export interface TipoEquipoItem {
  id: number;
  detalle: string;
}

interface TipoEquipoResponse {
  success: true;
  data: TipoEquipoItem[];
}

// ambitoId → types in use within an ámbito (filter). subcontratoId → ALL types of the
// subcontrato, used or not (edit: pick a valid-but-unused type). Backend prefers
// subcontratoId when both are sent.
export function useTipoEquipo(ambitoId?: number, subcontratoId?: number) {
  const params = new URLSearchParams();
  if (ambitoId) params.set('ambitoId', String(ambitoId));
  if (subcontratoId) params.set('subcontratoId', String(subcontratoId));
  const qs = params.toString();
  return useQuery({
    queryKey: ['catalogos', 'tipo-equipo', ambitoId ?? null, subcontratoId ?? null] as const,
    queryFn: () => apiClient<TipoEquipoResponse>(`/catalogos/tipo-equipo${qs ? `?${qs}` : ''}`).then((r) => r.data),
    staleTime: STALE,
    enabled: (ambitoId != null && ambitoId > 0) || (subcontratoId != null && subcontratoId > 0),
  });
}
